import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Download, Flame, MapPin, Search, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useEa } from "./EaContext";
import { MONTHS_FULL, fmtDay, gasLabel, pointSub } from "./eaFormat";

// Ricerca libera: codice POD/PDR/REMI, etichetta, via o città.
const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
export const matchPoint = (p, q) => !q || norm([p.identifier, p.remi_code, p.label, p.address, p.point_type].join(" ")).includes(norm(q.trim()));

export function EaPointPicker({ commodity }) {
  const name = (p) => (p?.commodity === "GAS" ? gasLabel(p) : p ? `${p.point_type} ${p.identifier}` : "—");
  const { nav, filters, setFilter } = useEa();
  const [q, setQ] = useState("");
  const points = (nav?.points || []).filter((p) => !commodity || p.commodity === commodity);
  const shown = points.filter((p) => matchPoint(p, q));
  const selected = filters.points ? filters.points.split(",").filter((c) => points.some((p) => p.code === c)) : [];
  const others = filters.points ? filters.points.split(",").filter((c) => c && !points.some((p) => p.code === c)) : [];
  const all = selected.length === 0;
  const save = (sel) => setFilter({ points: [...others, ...(sel.length === points.length ? [] : sel)].join(",") });
  const base = all ? points.map((p) => p.code) : selected;
  const toggle = (code) => {
    const next = base.includes(code) ? base.filter((c) => c !== code) : [...base, code];
    if (next.length) save(next);
  };
  const label = selected.length === 0 ? `Tutti i punti (${points.length})` : selected.length === 1
    ? name(points.find((p) => p.code === selected[0])) : `${selected.length} punti selezionati`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="ea-point-picker">
          <MapPin size={14} className="text-slate-400" /> {label}
          {[["POWER", Zap, "#8B32FB", "bg-violet-50"], ["GAS", Flame, "#3285FD", "bg-blue-50"]].map(([c, I, col, bg]) => {
            const n = base.filter((code) => points.find((p) => p.code === code)?.commodity === c).length;
            return points.some((p) => p.commodity === c) && (
              <span key={c} className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ${bg}`} style={{ color: col }} title={c === "POWER" ? "Punti Power selezionati" : "Punti Gas selezionati"} data-testid={`ea-point-count-${c.toLowerCase()}`}>
                <I size={12} /> {n}
              </span>
            );
          })}
          <ChevronDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <div className="relative mb-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca POD/PDR, via o città…"
            className="w-full h-8 pl-8 pr-2 rounded-md border border-slate-200 text-sm" data-testid="ea-point-search" />
        </div>
        <div className="flex items-center justify-between px-2 py-1.5">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer" data-testid="ea-point-all">
            <Checkbox checked={all} onCheckedChange={() => save([])} data-testid="ea-point-all-checkbox" /> Seleziona tutti
          </label>
          {q && shown.length > 0 && <button className="text-xs font-semibold text-blue-600" onClick={() => save(shown.map((p) => p.code))} data-testid="ea-point-select-found">Seleziona i {shown.length} trovati</button>}
        </div>
        <div className="max-h-72 overflow-y-auto">
          {!shown.length && <div className="px-2 py-3 text-sm text-slate-400" data-testid="ea-point-noresult">Nessun punto trovato</div>}
          {shown.map((p) => (
            <label key={p.code} className="flex items-start gap-2 px-2 py-2 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`ea-point-opt-${p.identifier}`}>
              <Checkbox checked={all || selected.includes(p.code)} onCheckedChange={() => toggle(p.code)} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">{name(p)}</span>
                <span className="block text-xs text-slate-500 truncate">{pointSub(p)}</span>
              </span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Codici selezionati della sola commodity richiesta.
export function useSelectedCodes(commodity) {
  const { filters, nav } = useEa();
  const valid = new Set((nav?.points || []).filter((p) => !commodity || p.commodity === commodity).map((p) => p.code));
  return (filters.points || "").split(",").filter((c) => valid.has(c)).join(",");
}

const STATUS_TXT = { invoiced: "Fatturato", pending: "In attesa fattura", partial: "Parziale", ACTUAL: "Consuntivo", FORECAST: "Previsionale", MIXED: "Misto" };

function exportValue(v) {
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (typeof v === "number") return Math.round(v * 10000) / 10000;
  if (typeof v === "string" && STATUS_TXT[v]) return STATUS_TXT[v];
  return v;
}

// Ogni riga riporta punto, indirizzo e periodo (mese per esteso + anno, oppure data).
export function EaExportButton({ filename, columns, rows, commodity, year, period, testid = "ea-export" }) {
  const { nav, filters } = useEa();
  const [busy, setBusy] = useState(false);
  const pts = (nav?.points || []).filter((p) => !commodity || p.commodity === commodity);
  const sel = filters.points ? pts.filter((p) => filters.points.split(",").includes(p.code)) : [];
  const scope = sel.length ? sel : pts;
  const yr = year || filters.year;
  const meta = (r) => ({
    _point: r.identifier ? `${r.point_type || (commodity === "GAS" ? "PDR" : "POD")} ${r.identifier}` : scope.map((p) => `${p.point_type} ${p.identifier}`).join(" + "),
    _address: r.identifier ? r.address || "" : scope.map((p) => p.address || "").join(" + "),
    _period: r.month ? `${MONTHS_FULL[r.month - 1]} ${yr}` : r.date ? fmtDay(r.date) : period || `Anno ${yr}`,
  });
  const cols = [{ key: "_point", label: commodity === "GAS" ? "PDR/REMI" : "POD" }, { key: "_address", label: "Indirizzo di fornitura" },
    { key: "_period", label: "Periodo" }, ...columns.filter((c) => !["month", "date", "identifier", "address"].includes(c.key))];
  const run = async (format) => {
    setBusy(true);
    try {
      const res = await api.post("/ea/export", { filename, format, columns: cols.map(({ key, label }) => ({ key, label })),
        rows: rows.map((r) => { const x = { ...r, ...meta(r) }; return Object.fromEntries(cols.map(({ key }) => [key, exportValue(x[key])])); }) }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${filename}.${format}`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Export non riuscito"); } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <span className="px-2.5 flex items-center text-slate-400"><Download size={14} /></span>
      {["csv", "xlsx"].map((f) => (
        <button key={f} disabled={busy || !rows?.length} onClick={() => run(f)} data-testid={`${testid}-${f}`}
          className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 border-l border-slate-200 disabled:opacity-40">
          {f === "csv" ? "CSV" : "Excel"}
        </button>
      ))}
    </div>
  );
}

const nums = (rows, k) => rows.map((r) => r[k]).filter((v) => v != null && !Number.isNaN(v));
// Totali di colonna: somma, media semplice o media ponderata (es. costo medio pesato sul consumo).
export const TOT = {
  sum: (k) => (rows) => { const v = nums(rows, k); return v.length ? v.reduce((a, b) => a + b, 0) : null; },
  avg: (k) => (rows) => { const v = nums(rows, k); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; },
  wavg: (k, w) => (rows) => {
    const rs = rows.filter((r) => r[k] != null && r[w]);
    const den = rs.reduce((a, r) => a + r[w], 0);
    return den ? rs.reduce((a, r) => a + r[k] * r[w], 0) / den : null;
  },
};

function TotalRow({ columns, rows, totalRow, label, testid }) {
  const vals = totalRow || Object.fromEntries(columns.filter((c) => c.total).map((c) => [c.key, (typeof c.total === "function" ? c.total : TOT[c.total === "avg" ? "avg" : "sum"](c.key))(rows)]));
  return (
    <tr className="border-t-2 border-slate-400 bg-slate-50 font-semibold text-slate-900" data-pdf-total data-testid={`${testid}-total`}>
      {columns.map((c, i) => {
        const v = vals[c.key];
        const f = c.totalFmt || c.fmt;
        return <td key={c.key} className={`px-4 py-2.5 whitespace-nowrap ${c.align === "left" ? "text-left font-sans" : "text-right"}`}>{i === 0 && v == null ? label : v == null ? "" : f ? f(v) : v}</td>;
      })}
    </tr>
  );
}

export function EaTable({ columns, rows, testid, rowKey = "code", onRowClick, initialSort, rowClass, totalRow, totalLabel = "Totale" }) {
  const hasTotal = rows.length > 1 && (totalRow || columns.some((c) => c.total));
  const [sort, setSort] = useState(initialSort || null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const { key, dir } = sort;
    return [...rows].sort((a, b) => {
      const x = a[key], y = b[key];
      if (x == null) return 1;
      if (y == null) return -1;
      return (x > y ? 1 : x < y ? -1 : 0) * (dir === "asc" ? 1 : -1);
    });
  }, [rows, sort]);
  const click = (key) => setSort((s) => ({ key, dir: s?.key === key && s.dir === "desc" ? "asc" : "desc" }));
  return (
    <div className="card-elevated overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            {columns.map((c) => (
              <th key={c.key} onClick={() => click(c.key)} className={`px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap ${c.align === "left" ? "text-left" : "text-right"}`}
                data-testid={`${testid}-sort-${c.key}`}>
                <span className="inline-flex items-center gap-1">{c.label}{sort?.key === c.key && (sort.dir === "desc" ? <ChevronDown size={12} /> : <ChevronUp size={12} />)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="font-mono-nums">
          {sorted.map((r, i) => (
            <tr key={r[rowKey] ?? i} onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={`border-t border-slate-100 hover:bg-slate-50/60 ${onRowClick ? "cursor-pointer" : ""} ${rowClass ? rowClass(r) : ""}`} data-testid={`${testid}-row-${i}`}>
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-2.5 whitespace-nowrap ${c.align === "left" ? "text-left font-sans" : "text-right"}`}>
                  {c.render ? c.render(r) : c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
          {hasTotal && <TotalRow columns={columns} rows={rows} totalRow={totalRow} label={totalLabel} testid={testid} />}
          {!sorted.length && <tr><td colSpan={columns.length} className="px-4 py-6 text-center text-slate-400 font-sans">Nessun dato</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
